import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useAuthService } from '@/services/auth';
import { api } from '@/services/api';
import { GET_CURRENT_USER_QUERY_KEY } from '@/hooks/api/users/useGetCurrentUser';

type CognitoAuthError = 'user-not-found' | 'access-forbidden' | 'verification-failed';

export const useOauth2Redirect = () => {
  const cognitoAuth = useAuth();
  const { logout } = useAuthService();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [authError, setAuthError] = useState<CognitoAuthError | undefined>();

  useEffect(() => {
    const handleAuthRedirect = async () => {
      if (cognitoAuth.isAuthenticated && cognitoAuth.user?.access_token && !isRedirecting) {
        setIsRedirecting(true);
        try {
          await queryClient.resetQueries({ queryKey: [GET_CURRENT_USER_QUERY_KEY] });
          await api.users.getCurrentUser();
          navigate('/tenants');
        } catch (error) {
          console.error('Authentication verification failed:', error);
          if (error instanceof AxiosError) {
            switch (error.response?.status) {
              case 401: {
                setAuthError('user-not-found');
                break;
              }
              case 403: {
                setAuthError('access-forbidden');
                break;
              }
              default: {
                setAuthError('verification-failed');
                break;
              }
            }
          } else {
            setAuthError('verification-failed');
          }
        } finally {
          setIsRedirecting(false);
        }
      }
    };

    handleAuthRedirect();
  }, [cognitoAuth.isAuthenticated, cognitoAuth.user?.access_token, queryClient, navigate]);

  const errorContentMap = {
    'user-not-found': {
      title: 'Account Not Found',
      message:
        "Your account was authenticated successfully, but you don't have access to this application. Please contact your administrator to request access.",
    },
    'access-forbidden': {
      title: 'Access Denied',
      message:
        "You don't have permission to access this application. Please contact your administrator for assistance.",
    },
    'verification-failed': {
      title: 'Verification Failed',
      message:
        'There was an error verifying your account. Please try signing in again, or contact support if the problem persists.',
    },
  };

  const errorContent = authError
    ? errorContentMap[authError]
    : {
        title: 'Authentication Error',
        message: 'An unexpected error occurred. Please try again.',
      };

  return {
    isRedirecting,
    authError,
    cognitoAuth,
    logout,
    errorContent,
  };
};
